import { Avatar, Tooltip } from 'flowbite-react';
import React, { useCallback, useEffect, useState } from 'react';
import { BsChevronRight } from 'react-icons/bs';
import classNames from 'classnames';
import { PositionStatus, PositionTitle, UIPosition, UIUser } from 'types';
import moment from 'moment';
import { generateCompanyLogoAvatarSrc } from '../../../utils/companyLogo';

import { generatePositionTitle } from 'shared-utils';
import { useNavigate } from 'react-router-dom';

interface Props {
	data: UIPosition;
	userDetail: UIUser;
	isAlreadyApplied?: boolean;
	isUserEligible: boolean;
}

const ShareablePositionCard = ({ data, userDetail, isAlreadyApplied, isUserEligible }: Props) => {
	const navigate = useNavigate();
	const [avatarImg, setAvatarImg] = useState<string>('');

	const generateAvatar = useCallback(async () => {
		const res = await generateCompanyLogoAvatarSrc(data?.company?.logo || '', data?.company?.url);
		setAvatarImg(res);
	}, [data?.company?.logo, data?.company?.url]);

	useEffect(() => {
		generateAvatar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data?.company?.logo, data?.company?.url]);

	const positionTitle = generatePositionTitle(data.title as PositionTitle);
	return (
		<div
			className="dark:bg-[#1F2A37] bg-[#FFF] rounded-[12px] cursor-pointer border dark:border-[#3F3F3F] border-[#EBEBF0] flex flex-col gap-[14px] mx-4 overflow-hidden"
			onClick={() => navigate(`/job/${data._id}`)}
		>
			<div className="w-full h-auto">
				<div className="px-[16px] pt-[16px] pb-[6px]">
					<div className="w-full flex flex-col gap-2">
						{/* Top section */}
						<div className="flex gap-2">
							{/* Company Logo Div */}
							<div className="flex items-center">
								<Avatar rounded alt="avatar of Jese" img={avatarImg} className="position-avatar object-contain" />
							</div>
							{/* Company Name / Rating / Position Name */}
							<div className="font-inter dark:text-[#F2F2F5] text-[#111E2C] flex flex-col justify-center grow-2">
								<div className="flex flex-row justify-between items-center">
									<div className="text-[20px] font-[600] sm:font-[700]">
										{data.company.name?.length > 14 ? (
											<Tooltip content={data.company.name} placement="top">
												{`${data.company.name.substring(0, 13)}...`}
											</Tooltip>
										) : (
											<>{data.company.name}</>
										)}
									</div>
								</div>
								<div className="dark:text-[#F2F2F5] text-[#555770] sm:text-[14px] text-[16px] font-[500]">
									{positionTitle?.length > 21 ? (
										<Tooltip content={positionTitle} placement="top">
											{`${positionTitle.substring(0, 18)}...`}
										</Tooltip>
									) : (
										<>{positionTitle}</>
									)}
								</div>
							</div>
						</div>
						{/* Middle Section */}
						<div className="flex md:mb-2 sm:gap-x-4 mt-1 sm:gap-y-[6px] flex-col  md:gap-[6px]">
							{/* Working Type / Experience */}
							<div className="flex flex-row gap-2 items-center">
								<div>
									<img
										src="/assets/briefcase.svg"
										alt="job"
										className="w-[20px] h-[20px] hidden dark:block object-contain"
									/>
									<img
										src="/assets/brief-light.svg"
										alt="job"
										className="w-[20px] h-[20px] block dark:hidden object-contain"
									/>
								</div>
								<div className="flex gap-1 items-center sm:text-[12px] text-[14px] dark:text-[#E4E4EB] text-[#555770]">
									<div className="pr-1">{data.workingMode}</div>
									<img src="/assets/dot.svg" alt="dot" width={6} height={6} className="object-contain" />
									<div>
										{data.positionRequirement.workExperience.min} - {data.positionRequirement.workExperience.max} Years
									</div>
								</div>
							</div>
							{/* Salary Package */}
							<div className="flex flex-row gap-2 items-center">
								<div>
									<img
										src="/assets/currencyRupee.svg"
										alt="rupee-currency"
										className="w-[20px] h-[20px] object-contain hidden dark:block"
									/>
									<img
										src="/assets/currency-light.svg"
										alt="dot"
										className="w-[20px] h-[20px] block dark:hidden object-contain"
									/>
								</div>
								<div className="flex items-center sm:text-[12px] text-[14px] dark:text-[#E4E4EB] text-[#555770]">
									{data.salary.min} - {data.salary.max} LPA
								</div>
							</div>
							{/* Job Location / Working Mode */}
							<div className="flex flex-row gap-2 items-center">
								<div>
									<img
										src="/assets/locationMarker.svg"
										alt="location"
										className="w-[20px] h-[20px] object-contain hidden dark:block"
									/>
									<img
										src="/assets/location-light.svg"
										alt="location"
										className="w-[20px] h-[20px] object-contain block dark:hidden"
									/>
								</div>
								<div className="flex gap-1 items-center sm:text-[12px] text-[14px] dark:text-[#E4E4EB] text-[#555770]">
									<div className="pr-1">{data.workLocation}</div>
									<img
										className="hukumu-position-card-working-mode-sibling"
										src="/assets/dot.svg"
										alt="dot"
										width={6}
										height={6}
									/>
									<div>{data.positionType}</div>
								</div>
							</div>
							<div className="text-[12px] font-[400] dark:text-[#A9EFF2] text-[#7777E7] md:flex hidden">
								Posted {moment(data.createdAt).fromNow()}
							</div>
						</div>
					</div>
				</div>
				{/* Bottom Section */}
				<div className=" border border-[#EBEBF0] dark:border-none flex justify-between items-center text-[14px] sm:text-[12px] font-[500] bg-[#FAFAFC] dark:bg-inherit p-[16px]">
					<div>
						{data.positionStatus === PositionStatus.OPEN ? (
							isUserEligible && !isAlreadyApplied ? (
								<p className={classNames('block greenish-gradient-text')}>Hurray! You’re eligible to apply</p>
							) : isUserEligible && isAlreadyApplied ? (
								<p className={classNames('block greenish-gradient-text')}>🎉 Congratulations on applying</p>
							) : (
								<p>
									Reach{' '}
									<span className="text-[#39D98A] inline">
										Level{' '}
										<img
											src="/assets/yellowLevelIcon.svg"
											alt={'levelup-icon'}
											className="w-[16px] inline h-[16px] object-contain"
										/>
									</span>{' '}
									{Math.round(data.positionRequirement.levelThreshold)}+ to apply{' '}
								</p>
							)
						) : (
							<p>
								Hired {data.hired && data.hired > 0 ? `${data.hired}` : null} aspirants at{' '}
								<span className="text-[#39D98A] inline">
									Level{' '}
									<img
										src="/assets/yellowLevelIcon.svg"
										alt={'levelup-icon'}
										className="w-[16px] inline h-[16px] object-contain"
									/>
								</span>{' '}
								{Math.round(data.positionRequirement.levelThreshold)}
							</p>
						)}
					</div>
					<div>
						<BsChevronRight size={20} color="#7777E7" />
					</div>
				</div>
			</div>
		</div>
	);
};

export default ShareablePositionCard;
